import { IEventEmitter, createEventEmitter } from './event-emitter';
import { TrackingEvent, TrackingEventType, EventPayloadMap, EventSchemaMap, EventPriority } from './types/events';
import { TrackingProvider } from './types/providers';
import {
  TrackingValidationError,
  TrackingProviderInitError,
  TrackingProviderNotFoundError,
  TrackingUnknownEventTypeError,
} from './types/errors';
import { TrackingMetadata } from './types/metadata';

type TrackingEventMap = {
  trackingEventReceived: TrackingEvent;
  trackingEventProcessed: TrackingEvent;
};

export class TrackingManager {
  private readonly eventEmitter: IEventEmitter<TrackingEventMap>;
  private readonly providers: Map<string, TrackingProvider> = new Map();
  private metadata: TrackingMetadata = {};

  constructor() {
    this.eventEmitter = createEventEmitter<TrackingEventMap>();
    this.initialize();
  }

  public registerProvider(provider: TrackingProvider): void {
    if (this.providers.has(provider.name)) {
      return;
    }

    this.providers.set(provider.name, provider);
    if (provider.initialize) {
      try {
        provider.initialize();
      } catch (error) {
        throw new TrackingProviderInitError(
          provider.name,
          error instanceof Error ? error.message : 'Unknown error',
          error instanceof Error ? error : undefined
        );
      }
    }
  }

  public unregisterProvider(providerName: string): void {
    const provider = this.providers.get(providerName);
    if (!provider) {
      throw new TrackingProviderNotFoundError(providerName);
    }

    if (provider.destroy) {
      try {
        provider.destroy();
      } catch (error) {
        console.error(`Error destroying provider ${providerName}:`, error);
      }
    }

    this.providers.delete(providerName);
  }

  public addMetadata(metadata: Partial<TrackingMetadata>): void {
    this.metadata = { ...this.metadata, ...metadata };
  }

  public track<T extends TrackingEventType>(
    type: T,
    payload?: EventPayloadMap[T],
    priority: EventPriority = EventPriority.INFO
  ): void {
    // Validate event type
    if (!EventSchemaMap[type]) {
      const error = new TrackingUnknownEventTypeError(type);
      this.error(error);
      return;
    }

    // Validate payload against schema
    const {
      success,
      data: validatedPayload,
      error: validationError,
    } = EventSchemaMap[type]
      ? EventSchemaMap[type].safeParse(payload)
      : { success: false, data: undefined, error: undefined };

    if (!success) {
      const error = new TrackingValidationError(`Validation failed for event type '${type}'`, validationError!);
      this.error(error);
      return;
    }

    const event: TrackingEvent = {
      type,
      timestamp: Date.now(),
      priority,
      payload: validatedPayload,
    };

    // Emit received event
    this.eventEmitter.emit('trackingEventReceived', event);

    for (const provider of this.providers.values()) {
      provider.receivedEvent?.(event, this.metadata);
    }

    // Emit processed event after all providers have processed it
    this.eventEmitter.emit('trackingEventProcessed', event);
  }

  // Log-level based tracking aliases with overloads for optional payloads
  public critical<T extends TrackingEventType>(name: T): void;
  public critical<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public critical<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.track(name, payload, EventPriority.CRITICAL);
  }

  public error(error: Error): void {
    this.track('Error', error, EventPriority.ERROR);
  }

  public warn<T extends TrackingEventType>(name: T): void;
  public warn<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public warn<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.track(name, payload, EventPriority.WARN);
  }

  public info<T extends TrackingEventType>(name: T): void;
  public info<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public info<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.track(name, payload, EventPriority.INFO);
  }

  public debug<T extends TrackingEventType>(name: T): void;
  public debug<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public debug<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.track(name, payload, EventPriority.DEBUG);
  }

  public trace<T extends TrackingEventType>(name: T): void;
  public trace<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public trace<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.track(name, payload, EventPriority.TRACE);
  }

  // Event listener methods
  public onEventReceived(callback: (event: TrackingEvent) => void): () => void {
    this.eventEmitter.on('trackingEventReceived', callback);
    return () => this.eventEmitter.off('trackingEventReceived', callback);
  }

  public offEventReceived(callback: (event: TrackingEvent) => void): void {
    this.eventEmitter.off('trackingEventReceived', callback);
  }

  public onEventProcessed(callback: (event: TrackingEvent) => void): () => void {
    this.eventEmitter.on('trackingEventProcessed', callback);
    return () => this.eventEmitter.off('trackingEventProcessed', callback);
  }
  public offEventProcessed(callback: (event: TrackingEvent) => void): void {
    this.eventEmitter.off('trackingEventProcessed', callback);
  }

  public initialize(): void {}

  public destroy(): void {
    // Clean up providers
    for (const provider of this.providers.values()) {
      if (provider.destroy) {
        try {
          provider.destroy();
        } catch (error) {
          console.error(`Error destroying provider ${provider.name}:`, error);
        }
      }
    }
    this.providers.clear();
  }
}
